// ─────────────────────────────────────────────
//  LoginPage — the front door. Opens in New User mode; existing users are
//  one click away.
//
//  Presentation rebuilt on the component library (Card/Field/Button)
//  and the light token palette (D-08, D-01a). Auth logic unchanged.
//
//  The new-user form deliberately does NOT create the Firebase Auth account.
//  It hands the credentials to /get-started, where the sandbox-or-organization
//  choice is made first. Signing anyone in here would let AuthContext bootstrap
//  a users/{uid} profile into a live hospital's Firestore in the gap before a
//  mode exists — the one thing the sandbox is built to prevent.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { login, resetPassword } from "../firebase/auth";
import { useAuth } from "../context/AuthContext";
import { Card, Field, Button } from "../components";
import { loginFooterText } from "../config/brand";
import tokens from "../theme/tokens";

const { color, space, radius, font } = tokens;

export default function LoginPage() {
  // A caller may say which form it needs. /get-started sends someone here after
  // "auth/email-already-in-use", and dropping them on Create Account — the form
  // whose default is what produced that error — would loop them straight back.
  // Seed-only (useState initialiser, not an effect): once the screen is open it
  // belongs to the person using it, and a later render must not yank the form
  // out from under someone who has since clicked "New here?".
  const { state: nav } = useLocation();
  const [email, setEmail]       = useState(nav?.email || "");
  const [password, setPassword] = useState("");
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetting, setResetting] = useState(false);
  // "new" | "existing" — a stranger is the common case at this door, so the
  // Create Account form is what loads unless a caller asked for the other one.
  const [mode, setMode]         = useState(nav?.mode === "existing" ? "existing" : "new");
  const { error: authError }    = useAuth();
  const navigate = useNavigate();

  const switchMode = (next) => {
    setError("");
    setResetMsg("");
    setMode(next);
  };

  // "Forgot password" — Firebase emails a reset link to the address. The link
  // itself is the temporary access: the user sets a permanent password from it,
  // then signs in normally. Requires an account to already exist for the email.
  const handleReset = async () => {
    setError("");
    setResetMsg("");
    if (!email.trim()) {
      setError("Enter your email above first, then click “Forgot password”.");
      return;
    }
    setResetting(true);
    try {
      await resetPassword(email.trim());
      setResetMsg(`If an account exists for ${email.trim()}, a password-reset link is on its way. Check your inbox (and spam).`);
    } catch (err) {
      if (err.code === "auth/invalid-email") setError("That email address is not valid.");
      else if (err.code === "auth/too-many-requests") setError("Too many attempts. Wait a few minutes and try again.");
      else setError("Could not send the reset email. Please try again.");
    } finally {
      setResetting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      // Signing in lands on the dashboard. The role-based redirects live on the
      // /home route itself (a Scheduler continues to /schedule, a wall device to
      // /board), so this stays one destination rather than a second copy of the
      // role rules that can drift from App.js.
      navigate("/home");
    } catch (err) {
      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Invalid email or password. Please try again.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many attempts. Wait a few minutes and try again.");
      } else {
        setError("Login error: " + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // New user. Validates only — the account is created on /get-started, AFTER
  // the sandbox-or-organization choice sets the boot flag.
  const handleCreate = (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Choose a password of at least 8 characters.");
      return;
    }
    navigate("/get-started", { state: { email: email.trim(), password } });
  };

  const formError = error || authError;
  const isNew = mode === "new";

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        <Card variant="elevated">
          <div style={styles.logo}>
            <div style={styles.logoIcon}>⚕</div>
            <div style={styles.logoText}>Anesthesia Team Control</div>
            <div style={styles.logoSub}>ATC · OR Team Management</div>
          </div>

          {isNew ? (
            <>
              <div style={styles.intro}>
                Create your account. You choose next whether to try ATC on your
                own or set up your organization.
              </div>

              <form onSubmit={handleCreate}>
                <Field
                  id="signup-email"
                  label="Email"
                  type="email"
                  placeholder="you@hospital.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Field
                  id="signup-password"
                  label="Password"
                  type="password"
                  placeholder="••••••••"
                  helperText="At least 8 characters."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                {formError ? (
                  <div style={styles.error} role="alert">{formError}</div>
                ) : null}
                <Button variant="primary" size="full" type="submit">
                  Create Account
                </Button>
              </form>

              {/* A button, not a link: it changes this screen, it does not
                  navigate anywhere. */}
              <button type="button" style={styles.link} onClick={() => switchMode("existing")}>
                Already have an account? Sign in
              </button>
            </>
          ) : (
            <>
              <form onSubmit={handleLogin}>
                <Field
                  id="login-email"
                  label="Email"
                  type="email"
                  placeholder="you@hospital.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <Field
                  id="login-password"
                  label="Password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                {formError ? (
                  <div style={styles.error} role="alert">{formError}</div>
                ) : null}
                {resetMsg ? (
                  <div style={styles.notice} role="status">{resetMsg}</div>
                ) : null}
                <Button variant="primary" size="full" type="submit" loading={loading}>
                  {loading ? "Signing in…" : "Sign In"}
                </Button>
              </form>

              <button type="button" style={styles.link} onClick={handleReset} disabled={resetting}>
                {resetting ? "Sending reset link…" : "Forgot password?"}
              </button>

              <button type="button" style={styles.link} onClick={() => switchMode("new")}>
                New here? Create an account
              </button>
            </>
          )}

          {/* The operator's name belongs on SIGN IN and nowhere else.
              Returning staff benefit from seeing their own hospital on the
              screen where they hand over a password — that is the whole point
              of Track C. But CREATE ACCOUNT is the product's front door, open
              to anyone who installs the app, and a hospital's name there reads
              as a claim of affiliation with a company that does not own this
              product. A stranger evaluating ATC must never be shown another
              organization's brand.

              Rules gate `facilities` reads on isAuth(), so this name can only
              come from build config or the per-device cache. Unset = a neutral
              line, never another tenant's brand. */}
          <div style={styles.footer}>
            {mode === "existing" ? loginFooterText() : "Secure login"}
          </div>
        </Card>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: color.surface.base,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: space.md,
  },
  shell: { width: 400, maxWidth: "95vw" },
  logo: { textAlign: "center", marginBottom: space.lg },
  logoIcon: { fontSize: 42, marginBottom: space.sm },
  logoText: {
    fontSize: font.size.display,
    fontWeight: font.weight.bold,
    color: color.accent.primary,
    fontFamily: font.family,
  },
  logoSub: {
    fontSize: font.size.label,
    color: color.text.muted,
    marginTop: space.xs,
    letterSpacing: 1,
    textTransform: "uppercase",
    fontFamily: font.family,
  },
  error: {
    background: color.surface.error,
    border: "1px solid " + color.accent.error,
    // Ink, not accent.error: #dc2626 on this ground is 4.41:1 and FAILS AA for
    // normal text (tokens.js used to assert 4.95:1 and pass — it was wrong, and
    // four screens had been consolidated onto that sentence). accent.error stays
    // correct as the BORDER above; it is only as text that it falls short.
    color: color.surface.errorInk,
    borderRadius: radius.sm,
    padding: "8px 12px",
    fontSize: font.size.label,
    marginBottom: space.sm,
    fontFamily: font.family,
  },
  footer: {
    textAlign: "center",
    fontSize: font.size.label,
    color: color.text.muted,
    marginTop: space.md,
    fontFamily: font.family,
  },
  link: {
    display: "block",
    // 44 px minimum touch target (WCAG 2.2 AA); the padding keeps the
    // underlined text vertically centred inside it.
    minHeight: 44,
    width: "100%",
    padding: space.sm,
    margin: `${space.sm}px auto 0`,
    background: "transparent",
    border: "none",
    color: color.accent.primary,
    cursor: "pointer",
    fontSize: font.size.label,
    fontFamily: font.family,
    textDecoration: "underline",
  },
  intro: {
    fontSize: font.size.label,
    lineHeight: font.lineHeight.normal,
    color: color.text.secondary,
    marginBottom: space.md,
    fontFamily: font.family,
  },
  notice: {
    background: "#f0f7ff",
    border: "1px solid " + color.accent.primary,
    color: color.text.primary,
    borderRadius: radius.sm,
    padding: "8px 12px",
    fontSize: font.size.label,
    marginBottom: space.sm,
    fontFamily: font.family,
  },
};
